// Calendar events: plain functions over a list kept on the device (revault:calendar), so the tool
// and the tests share them. An event is { id, title, date, repeat, until, skip }:
//   date   "YYYY-MM-DD", the first day,
//   repeat one of REPEATS,
//   until  the last day it can repeat on ("" for no end),
//   skip   days taken out of a repeating event one at a time.
// Day keys are local dates, stepped at noon so a DST change never skips or doubles a day.
export const KEY = "revault:calendar";
export const MAX_EVENTS = 300;
export const MAX_TITLE = 80;
const MAX_SKIP = 100;
const KEY_RE = /^\d{4}-\d{2}-\d{2}$/;

export const REPEATS = [
	["none", "Does not repeat"],
	["daily", "Every day"],
	["weekdays", "Every weekday (Mon to Fri)"],
	["weekly", "Every week"],
	["biweekly", "Every 2 weeks"],
	["monthly", "Every month"],
];
const REPEAT_IDS = REPEATS.map((r) => r[0]);

const p2 = (n) => (n < 10 ? "0" : "") + n;
export const dayKey = (d) => d.getFullYear() + "-" + p2(d.getMonth() + 1) + "-" + p2(d.getDate());
export const fromKey = (key) => { const [y, m, d] = key.split("-").map(Number); return new Date(y, m - 1, d, 12); };
const diffDays = (a, b) => Math.round((fromKey(b) - fromKey(a)) / 864e5);
const validKey = (k) => typeof k === "string" && KEY_RE.test(k) && dayKey(fromKey(k)) === k;

// Reads tolerate anything; bad entries are dropped.
export function cleanEvents(raw) {
	if (!Array.isArray(raw)) return [];
	const out = [];
	for (const e of raw) {
		if (!e || typeof e !== "object" || !validKey(e.date)) continue;
		const title = String(e.title || "").trim().slice(0, MAX_TITLE);
		if (!title) continue;
		const repeat = REPEAT_IDS.includes(e.repeat) ? e.repeat : "none";
		const until = repeat !== "none" && validKey(e.until) && e.until >= e.date ? e.until : "";
		const skip = repeat !== "none" && Array.isArray(e.skip) ? e.skip.filter(validKey).slice(-MAX_SKIP) : [];
		out.push({ id: String(e.id || "").slice(0, 24) || Math.random().toString(36).slice(2, 10), title, date: e.date, repeat, until, skip });
		if (out.length >= MAX_EVENTS) break;
	}
	return out;
}

// Does this event fall on `key`? A monthly event started on the 31st lands on the last day of
// shorter months.
export function occursOn(ev, key) {
	if (key < ev.date) return false;
	if (ev.repeat === "none") return key === ev.date;
	if (ev.until && key > ev.until) return false;
	if (ev.skip && ev.skip.includes(key)) return false;
	const d = fromKey(key);
	switch (ev.repeat) {
		case "daily": return true;
		case "weekdays": return d.getDay() >= 1 && d.getDay() <= 5;
		case "weekly": return diffDays(ev.date, key) % 7 === 0;
		case "biweekly": return diffDays(ev.date, key) % 14 === 0;
		case "monthly": {
			const want = fromKey(ev.date).getDate();
			const last = new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate();
			return d.getDate() === Math.min(want, last);
		}
		default: return false;
	}
}

export const eventsOn = (list, key) => list.filter((e) => occursOn(e, key)).sort((a, b) => a.title.localeCompare(b.title));

// A new event from the form, or an error to show. `now` only makes the id.
export function makeEvent({ title, date, repeat, until }, now = Date.now()) {
	const t = String(title || "").trim();
	if (!t) return { error: "Give it a name." };
	if (!validKey(date)) return { error: "Pick a date." };
	const r = REPEAT_IDS.includes(repeat) ? repeat : "none";
	if (r !== "none" && until && !validKey(until)) return { error: "Pick an end date, or leave it empty." };
	if (r !== "none" && until && until < date) return { error: "The end date is before the first day." };
	return { event: { id: now.toString(36) + Math.random().toString(36).slice(2, 6), title: t.slice(0, MAX_TITLE), date, repeat: r, until: r === "none" ? "" : until || "", skip: [] } };
}

// Take one day out of a repeating event (or the whole event when it doesn't repeat).
export function removeDay(list, id, key) {
	return list.flatMap((e) => {
		if (e.id !== id) return [e];
		if (e.repeat === "none") return [];
		return [{ ...e, skip: [...e.skip, key].slice(-MAX_SKIP) }];
	});
}
export const removeEvent = (list, id) => list.filter((e) => e.id !== id);

// "Every week until Dec 18" and the like, for the list under the month.
const SHORT_MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
export function repeatText(ev) {
	if (ev.repeat === "none") return "";
	const base = REPEATS.find((r) => r[0] === ev.repeat)[1];
	if (!ev.until) return base;
	const u = fromKey(ev.until);
	return base + " until " + SHORT_MONTHS[u.getMonth()] + " " + u.getDate() + (u.getFullYear() !== fromKey(ev.date).getFullYear() ? ", " + u.getFullYear() : "");
}

// The "Days between" and "Add / subtract days" boxes. Both read the date inputs as local days
// (new Date("2026-10-24") is UTC midnight, the evening before west of UTC).
export function daysBetween(a, b) {
	return validKey(a) && validKey(b) ? diffDays(a, b) : null;
}
export function addDaysTo(key, n) {
	if (!validKey(key)) return null;
	const d = fromKey(key);
	d.setDate(d.getDate() + (Number.isFinite(n) ? n : 0));
	return d;
}
